import { db } from "../../firebase";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/**
 * Verifica si un valor es una cadena no vacía
 */
function isStringNonEmpty(v) {
  return typeof v === "string" && v.trim().length > 0;
}

/**
 * Obtiene los campos nombres y cargo de un usuario por su UID
 */
async function fetchUserFieldsByUid(uid) {
  try {
    if (!isStringNonEmpty(uid)) return { nombres: "", cargo: "" };
    const doc = await db.collection("usuarios").doc(uid.trim()).get();
    if (!doc.exists) return { nombres: "", cargo: "" };
    const data = doc.data() || {};
    return {
      nombres: String(data.nombres || "").trim(),
      cargo: String(data.cargo || "").trim(),
    };
  } catch (_) {
    return { nombres: "", cargo: "" };
  }
}

/**
 * Exporta la colección de casos a Excel con la misma lógica que GeneradordeExcel.js
 * - Calcula por UID (uidinvestigador/uidabogado): Total Casos, Casos Activos (estado == 'Recibido'), Casos Cerrados
 * - Concatena detalle de casos activos (placa,caso,estado) separados por '||'
 * - Resuelve Investigador y Cargo desde colección 'usuarios'
 *
 * @param {string} collection - Nombre de la colección (por defecto 'casos')
 * @param {string} fileName - Nombre del archivo Excel
 * @param {number|null} createdStartMs - Timestamp de inicio en milisegundos (opcional)
 * @param {number|null} createdEndMs - Timestamp de fin en milisegundos (opcional)
 * @param {function} onProgress - Callback para reportar progreso (count) => void
 * @returns {Promise<{count: number}>}
 */
export async function exportCollectionToExcel(
  collection = "casos",
  fileName = "total_casos_por_uid.xlsx",
  createdStartMs = null,
  createdEndMs = null,
  onProgress = null
) {
  console.log(
    `📊 Iniciando exportación de colección '${collection}' con lógica de GeneradordeExcel.js...`
  );

  // Construir consulta
  let query = db.collection(collection);
  if (typeof createdStartMs === "number") {
    query = query.where("creado", ">=", createdStartMs);
  }
  if (typeof createdEndMs === "number") {
    query = query.where("creado", "<=", createdEndMs);
  }

  // Mapas para agrupar por UID
  const totalByUid = new Map();
  const activeByUid = new Map();
  const closedByUid = new Map();
  const activeDetailsByUid = new Map(); // uid -> string[] ("placa,caso,estado")

  // Leer todos los documentos
  const snaps = await query.get();
  let readCount = 0;

  snaps.docs.forEach((doc) => {
    readCount++;
    const data = doc.data() || {};
    const uidInv = data.uidinvestigador;
    const uidAbg = data.uidabogado;
    const rawEstado = data.estado;
    const isRecibido =
      isStringNonEmpty(rawEstado) &&
      String(rawEstado).trim().toLowerCase() === "recibido";
    const placa = String(data.placa || "").trim();
    const caso = String(data.caso || "").trim();
    const estado = String(rawEstado || "").trim();
    const det = `${placa},${caso},${estado}`;

    // Función auxiliar para procesar un UID
    const consumeUid = (uid) => {
      if (!isStringNonEmpty(uid)) return;
      const u = uid.trim();
      totalByUid.set(u, (totalByUid.get(u) || 0) + 1);
      if (isRecibido) {
        activeByUid.set(u, (activeByUid.get(u) || 0) + 1);
        const arr = activeDetailsByUid.get(u) || [];
        arr.push(det);
        activeDetailsByUid.set(u, arr);
      } else {
        closedByUid.set(u, (closedByUid.get(u) || 0) + 1);
      }
    };

    consumeUid(uidInv);
    consumeUid(uidAbg);

    if (onProgress) {
      onProgress(readCount);
    }
  });

  console.log(`📄 Documentos leídos: ${readCount}`);
  console.log(`👤 UIDs distintos: ${totalByUid.size}`);

  // Resolver información de usuarios por UID
  const rows = [];
  const uids = Array.from(totalByUid.keys());
  const userMap = new Map();

  console.log(`🔍 Resolviendo información de usuarios...`);
  await Promise.all(
    uids.map(async (uid) => {
      userMap.set(uid, await fetchUserFieldsByUid(uid));
    })
  );

  // Construir filas
  for (const uid of uids) {
    const total = totalByUid.get(uid) || 0;
    const activos = activeByUid.get(uid) || 0;
    const cerrados = closedByUid.get(uid) || 0;
    const detalleList = activeDetailsByUid.get(uid) || [];
    const detalleJoined = detalleList.join("||");
    const { nombres, cargo } = userMap.get(uid) || {
      nombres: "",
      cargo: "",
    };

    rows.push({
      Investigador: nombres,
      Cargo: cargo,
      UID: uid,
      "Total Casos": total,
      "Casos Activos": activos,
      "Casos Activos Detalle (placa,caso,estado) sep=||": detalleJoined,
      "Casos Cerrados": cerrados,
    });
  }

  // Ordenar por Total Casos desc, UID asc
  rows.sort((a, b) => {
    if (b["Total Casos"] !== a["Total Casos"])
      return b["Total Casos"] - a["Total Casos"];
    return String(a.UID).localeCompare(String(b.UID));
  });

  console.log(`✅ Generando archivo Excel...`);

  // Generar Excel
  const ws = XLSX.utils.json_to_sheet(rows, {
    header: [
      "Investigador",
      "Cargo",
      "UID",
      "Total Casos",
      "Casos Activos",
      "Casos Activos Detalle (placa,caso,estado) sep=||",
      "Casos Cerrados",
    ],
    skipHeader: false,
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "totales");

  // Convertir a buffer y descargar
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  // Generar nombre de archivo con timestamp si no se proporciona
  const timestamp = new Date()
    .toISOString()
    .replace(/[-:TZ]/g, "")
    .slice(0, 15);
  const finalFileName = fileName || `total_casos_por_uid_${timestamp}.xlsx`;

  // Descargar archivo
  saveAs(blob, finalFileName);

  console.log(`✅ Archivo "${finalFileName}" descargado exitosamente`);

  return {
    count: readCount,
    fileName: finalFileName,
    rowsCount: rows.length,
  };
}
